import { Leaf, Target, Eye, Heart, Globe, Users, Award, Lightbulb } from 'lucide-react'

const MissionVision = () => {
  const values = [
    {
      icon: Heart,
      title: 'Sustainability',
      description: 'We are committed to environmentally responsible practices that protect our planet for future generations.'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'We believe in supporting farming communities and building lasting relationships with our customers.'
    },
    {
      icon: Award,
      title: 'Quality',
      description: 'We maintain the highest standards in product development and manufacturing processes.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We continuously research and develop new solutions to meet evolving agricultural challenges.'
    }
  ]

  const commitments = [
    'Reducing carbon footprint through sustainable manufacturing',
    'Supporting organic and regenerative farming practices',
    'Investing in research for eco-friendly solutions',
    'Educating farmers on sustainable agriculture techniques',
    'Partnering with local communities for environmental conservation'
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-white py-16">
        <div className="container-custom text-center">
          <h1 className="section-heading mb-4">Mission & Vision</h1>
          <p className="subheading max-w-3xl">
            Our commitment to sustainable agriculture and helping farmers achieve better yields
            while protecting our environment for future generations.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="bg-earth-green p-3 rounded-lg mr-4">
                    <Target className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  To provide innovative, sustainable agricultural solutions that help farmers
                  maximize their yields while preserving the environment. We are dedicated to
                  developing premium fertilizers and plant nutrients that promote healthy soil,
                  reduce environmental impact, and support the long-term success of farming communities worldwide.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Through continuous research, education, and partnership with farmers, we strive
                  to be the leading provider of sustainable agricultural solutions that balance
                  productivity with environmental responsibility.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div
                className="h-96 rounded-xl bg-cover bg-center"
                style={{
                  backgroundImage: "url('https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=600&fit=crop')"
                }}
              >
                <div className="h-full w-full bg-black bg-opacity-30 rounded-xl flex items-center justify-center">
                  <div className="text-center text-white">
                    <Leaf className="h-16 w-16 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold">Growing the Future</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div
                className="h-96 rounded-xl bg-cover bg-center"
                style={{
                  backgroundImage: "url('https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=800&h=600&fit=crop')"
                }}
              >
                <div className="h-full w-full bg-black bg-opacity-30 rounded-xl flex items-center justify-center">
                  <div className="text-center text-white">
                    <Globe className="h-16 w-16 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold">Sustainable World</h3>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-white p-8 rounded-xl border border-gray-200">
                <div className="flex items-center mb-6">
                  <div className="bg-olive p-3 rounded-lg mr-4">
                    <Eye className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  To create a world where agriculture thrives in harmony with nature, where every
                  farmer has access to the tools and knowledge needed to grow abundant, healthy
                  crops while protecting and restoring our planet's ecosystems.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We envision a future where sustainable farming practices are the norm, where
                  soil health is prioritized, and where agricultural productivity and environmental
                  stewardship go hand in hand. Through innovation and collaboration, we aim to
                  transform the agricultural industry for the betterment of all.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at Novatarra
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow">
                  <div className="bg-earth-green p-4 rounded-full w-fit mx-auto mb-4">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Sustainability Commitments */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Sustainability Commitments
              </h2>
              <p className="text-xl text-gray-600">
                Our promise to protect the environment and support sustainable agriculture
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {commitments.map((commitment, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="bg-earth-green p-1 rounded-full mt-1">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <p className="text-gray-700">{commitment}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding hero-gradient">
        <div className="container-custom text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            Join Us in Building a Sustainable Future
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-200">
            Together, we can create a more sustainable agricultural industry that benefits
            farmers, consumers, and the environment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/products"
              className="bg-white text-earth-green hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Explore Our Products
            </a>
            <a
              href="/about#contact"
              className="border-2 border-white text-white hover:bg-white hover:text-earth-green font-semibold py-3 px-8 rounded-lg transition-all duration-200"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default MissionVision
